import { useState, useEffect } from 'react';
import { Loader2, Clock, Calendar, Mic, FileText, Globe, Stethoscope, Euro, Users, Download, Phone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';
import type { VerbruikGroep, VerbruikGroepen } from '@/lib/api-types';

/** Eén maand van één praktijk, zoals GET /api/admin/usage hem levert. */
interface Maand {
  month: string;
  purchase: number;
  selling: number;
  entries: number;
}

const USAGE_TYPE_LABELS: Record<string, { label: string; icon: typeof Mic }> = {
  speech_recording: { label: 'Spraakopname', icon: Mic },
  soep_transcription: { label: 'SOEP-transcriptie', icon: FileText },
  translation: { label: 'Vertaling', icon: Globe },
  icpc_suggestion: { label: 'ICPC-suggestie', icon: Stethoscope },
  patient_summary: { label: 'Patiëntsamenvatting (PDF)', icon: FileText },
  acs_call: { label: 'Telefoongesprek (ACS)', icon: Phone },
};

function formatDuration(totalSeconds: number): string {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const secs = Math.round(totalSeconds % 60);
  if (hours > 0) return `${hours}u ${minutes}m ${secs}s`;
  if (minutes > 0) return `${minutes}m ${secs}s`;
  return `${secs}s`;
}

function formatMonth(monthStr: string): string {
  const [year, month] = monthStr.split('-');
  const date = new Date(Number(year), Number(month) - 1);
  return date.toLocaleDateString('nl-NL', { month: 'long', year: 'numeric' });
}

export function AiCostsTab({ practiceId, practiceName, agbCode, afasId }: { practiceId?: string; practiceName?: string; agbCode?: string; afasId?: string | null }) {
  const [totals, setTotals] = useState<VerbruikGroep | null>(null);
  const [perType, setPerType] = useState<VerbruikGroep[]>([]);
  const [perGebruiker, setPerGebruiker] = useState<VerbruikGroep[]>([]);
  const [maanden, setMaanden] = useState<Maand[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!practiceId) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    void (async () => {
      try {
        // De bedragen komen van de server, over al het verbruik van de praktijk.
        // Dit scherm haalde de laatste 2000 regels op en telde die zelf op; bij een
        // praktijk met meer verbruik waren totalen en maanden dan te laag.
        //
        // De maanden komen uit GET /admin/usage, net als de export voor alle
        // praktijken: dan geven de twee exports voor dezelfde maand hetzelfde bedrag.
        const [overzicht, typen, gebruikers] = await Promise.all([
          api.get<{ practices: Array<{ months: Maand[] }> }>('/admin/usage', {
            query: { practiceId },
          }),
          api.get<VerbruikGroepen>('/admin/usage/breakdown', {
            query: { practiceId, groupBy: 'type' },
          }),
          api.get<VerbruikGroepen>('/admin/usage/breakdown', {
            query: { practiceId, groupBy: 'user' },
          }),
        ]);
        setMaanden(overzicht.practices[0]?.months ?? []);
        setTotals(typen.totals);
        setPerType(typen.groups);
        setPerGebruiker(gebruikers.groups);
      } catch {
        // Een leeg overzicht is hier beter dan een foutmelding: dit tabblad staat in
        // een dialoog naast de praktijkinstellingen, die wel bruikbaar blijven.
        setMaanden([]);
        setTotals(null);
        setPerType([]);
        setPerGebruiker([]);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [practiceId]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!practiceId) {
    return <p className="text-sm text-muted-foreground italic">Geen praktijk geselecteerd.</p>;
  }

  const totalSeconds = totals?.seconds ?? 0;
  const totalCost = totals?.selling ?? 0;
  const totalPurchase = totals?.purchase ?? 0;

  const sortedMonths = [...maanden].sort((a, b) => b.month.localeCompare(a.month));

  const handleExportExcel = () => {
    if (sortedMonths.length === 0) return;
    const sep = ';';
    const header = ['AGBCode', 'Praktijknaam', 'AFAS ID', 'InkoopExBtw', 'VerkoopExBTW', 'MaandPeriode', 'JaarPeriode'].join(sep);
    const rows = sortedMonths.map((data) => {
      const [year, m] = data.month.split('-');
      return [
        agbCode || '',
        practiceName || '',
        afasId || '',
        data.purchase.toFixed(4).replace('.', ','),
        data.selling.toFixed(4).replace('.', ','),
        m,
        year,
      ].join(sep);
    });
    const bom = '\uFEFF';
    const csv = bom + [header, ...rows].join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `kosten-omzet-${(practiceName || 'praktijk').replace(/\s+/g, '-').toLowerCase()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4">
      {/* Export button */}
      {sortedMonths.length > 0 && (
        <div className="flex justify-end">
          <Button variant="outline" size="sm" onClick={handleExportExcel}>
            <Download className="h-4 w-4 mr-1.5" />
            Exporteer naar Excel
          </Button>
        </div>
      )}
      <div className="grid grid-cols-3 gap-3">
        <div className="rounded-lg border bg-card p-4">
          <div className="flex items-center gap-2 mb-1">
            <Clock className="h-4 w-4 text-primary" />
            <span className="text-sm font-medium">Totaal AI-gebruik</span>
          </div>
          <p className="text-2xl font-bold">{formatDuration(totalSeconds)}</p>
          <p className="text-xs text-muted-foreground mt-1">
            {totals?.entries ?? 0} AI-acties totaal
          </p>
        </div>
        <div className="rounded-lg border bg-card p-4">
          <div className="flex items-center gap-2 mb-1">
            <Euro className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium">Inkoopkosten</span>
          </div>
          <p className="text-2xl font-bold">€{totalPurchase.toFixed(2)}</p>
          <p className="text-xs text-muted-foreground mt-1">totale inkoop</p>
        </div>
        <div className="rounded-lg border bg-card p-4">
          <div className="flex items-center gap-2 mb-1">
            <Euro className="h-4 w-4 text-primary" />
            <span className="text-sm font-medium">Verkoopomzet</span>
          </div>
          <p className="text-2xl font-bold">€{totalCost.toFixed(2)}</p>
          <p className="text-xs text-muted-foreground mt-1">marge €{(totalCost - totalPurchase).toFixed(2)}</p>
        </div>
      </div>

      {/* Per-type breakdown */}
      <div>
        <span className="text-sm font-medium">Per type</span>
        <div className="mt-2 space-y-1">
          {perType.map((data) => {
            const type = data.usageType ?? 'speech_recording';
            const config = USAGE_TYPE_LABELS[type] || { label: type, icon: Clock };
            const Icon = config.icon;
            return (
              <div key={type} className="flex items-center justify-between py-1.5 px-2 rounded hover:bg-muted/50">
                <div className="flex items-center gap-2">
                  <Icon className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="text-sm">{config.label}</span>
                </div>
                <div className="text-right">
                  <span className="text-sm font-medium">
                    {type === 'speech_recording' || type === 'acs_call' ? formatDuration(data.seconds) : `${data.entries}×`}
                  </span>
                  <p className="text-[10px] text-muted-foreground">inkoop €{data.purchase.toFixed(4)} · verkoop €{data.selling.toFixed(4)}</p>
                </div>
              </div>
            );
          })}
          {perType.length === 0 && (
            <p className="text-sm text-muted-foreground italic">Nog geen AI-gebruik geregistreerd.</p>
          )}
        </div>
      </div>

      {/* Per-user breakdown */}
      {perGebruiker.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Users className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium">Per gebruiker</span>
          </div>
          <div className="space-y-1">
            {perGebruiker.map((data) => (
              <div
                key={data.practiceUserId ?? 'geen'}
                className="flex items-center justify-between py-1.5 px-2 rounded hover:bg-muted/50"
              >
                <span className="text-sm">{data.userName || data.practiceUserId || 'Onbekende gebruiker'}</span>
                <div className="text-right">
                  <span className="text-sm font-medium">{data.entries} acties</span>
                  <p className="text-[10px] text-muted-foreground">inkoop €{data.purchase.toFixed(2)} · verkoop €{data.selling.toFixed(2)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Monthly breakdown */}
      <div>
        <div className="flex items-center gap-2 mb-2">
          <Calendar className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-medium">Per maand</span>
        </div>
        
        {sortedMonths.length === 0 ? (
          <p className="text-sm text-muted-foreground italic">
            Nog geen gebruik geregistreerd.
          </p>
        ) : (
          <div className="space-y-1">
            {sortedMonths.map((data) => (
              <div key={data.month} className="flex items-center justify-between py-1.5 px-2 rounded hover:bg-muted/50">
                <span className="text-sm capitalize">{formatMonth(data.month)}</span>
                <div className="text-right">
                  <span className="text-sm font-medium">{data.entries} acties</span>
                  <p className="text-[10px] text-muted-foreground">inkoop €{data.purchase.toFixed(2)} · verkoop €{data.selling.toFixed(2)}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
